import { alpha, Box, Chip, Stack, Typography } from '@mui/material';
import {
  BadgeOutlined,
  CheckCircle,
  ErrorOutline,
  EventAvailableOutlined,
  MailOutline,
  VpnKeyOutlined,
} from '@mui/icons-material';
import moment from 'moment';
import React, { useMemo } from 'react';
import { LoadingMask } from 'src/Common/Components/LoadingMask';
import { UserAvatar } from 'src/Common/Components/UserAvatar';
import { SettingsSection } from '../SettingsSection';
import { useSettings } from '../SettingsContext';

const StatTile = ({
  icon,
  label,
  value,
  hint,
  hintColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
  hintColor?: string;
}): React.JSX.Element => (
  <Stack
    direction={'row'}
    spacing={1.5}
    alignItems={'center'}
    sx={{
      flex: 1,
      minWidth: 0,
      p: 2,
      borderRadius: 1,
      border: 1,
      borderColor: 'divider',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        flexShrink: 0,
        color: 'primary.main',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
        '& .MuiSvgIcon-root': { fontSize: 20 },
      }}
    >
      {icon}
    </Box>
    <Stack direction={'column'} spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant={'caption'} color={'text.secondary'} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant={'subtitle1'} fontWeight={700} noWrap>
        {value}
      </Typography>
      {hint && (
        <Typography variant={'caption'} sx={{ color: hintColor ?? 'text.secondary' }} noWrap>
          {hint}
        </Typography>
      )}
    </Stack>
  </Stack>
);

export const ProfileSection = (): React.JSX.Element => {
  const { user, isLoading } = useSettings();

  const validity = useMemo(() => {
    if (!user) {
      return undefined;
    }
    const validDate = moment(user.validUntil);
    const isExpired = validDate.isBefore(moment.now());
    return {
      isExpired,
      label: isExpired ? 'Expired' : 'Active',
      date: validDate.format('LL'),
      relative: `${isExpired ? 'Expired' : 'Expires'} ${validDate.fromNow()}`,
    };
  }, [user]);

  return (
    <SettingsSection title={'Profile'} description={'Your account identity.'}>
      {isLoading || !user || !validity ? (
        <LoadingMask width={60} height={60} />
      ) : (
        <Stack direction={'column'} spacing={3}>
          {/* Hero */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: 1,
              p: { xs: 3, sm: 4 },
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(
                  theme.palette.secondary.main,
                  0.14,
                )} 100%)`,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 3 }}
              alignItems={'center'}
              textAlign={{ xs: 'center', sm: 'left' }}
            >
              <UserAvatar
                username={user.username}
                size={'large'}
                sx={{
                  width: 88,
                  height: 88,
                  fontSize: 36,
                  opacity: 1,
                  border: 4,
                  borderColor: 'background.paper',
                  boxShadow: (theme) => theme.shadows[4],
                }}
              />
              <Stack
                direction={'column'}
                spacing={1}
                alignItems={{ xs: 'center', sm: 'flex-start' }}
                sx={{ minWidth: 0 }}
              >
                <Typography variant={'h4'} fontWeight={800} noWrap sx={{ lineHeight: 1.1 }}>
                  {user.username}
                </Typography>
                <Stack direction={'row'} spacing={1} alignItems={'center'} sx={{ color: 'text.secondary' }}>
                  <MailOutline fontSize={'small'} />
                  <Typography variant={'body2'} noWrap>
                    {user.email}
                  </Typography>
                </Stack>
                <Chip
                  size={'small'}
                  color={validity.isExpired ? 'error' : 'success'}
                  variant={validity.isExpired ? 'filled' : 'outlined'}
                  icon={validity.isExpired ? <ErrorOutline /> : <CheckCircle />}
                  label={validity.label}
                  sx={{ fontWeight: 700, mt: 0.5 }}
                />
              </Stack>
            </Stack>
          </Box>

          {/* Stats */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <StatTile
              icon={<EventAvailableOutlined />}
              label={'Valid until'}
              value={validity.date}
              hint={validity.relative}
              hintColor={validity.isExpired ? 'error.main' : undefined}
            />
            <StatTile icon={<BadgeOutlined />} label={'Roles'} value={user.roleMappings.length} />
            <StatTile icon={<VpnKeyOutlined />} label={'Permissions'} value={user.permissions.length} />
          </Stack>
        </Stack>
      )}
    </SettingsSection>
  );
};
